import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { checkInputErrorRequired, useViewport } from '@chilecompra/react-kit'
import { Card, Input, Typography, useCheckBox, useInput } from '@chilecompra/react-kit/components'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import { CURRENCY_SIGNS } from '../../config/settings/constants'
import { CheckBox, GridContainer, GridItem } from './QuoteBidSeller.styles'
import { AmountPrefix } from '../QuoteStepOne/QuoteStepOne.styles'

/**
 * The Quote Bid List Product's component.
 */
const QuoteBidProductsList = props => {
  const { description, id, margin, name, quantity, setSubtotal, subtotal, typeCurrency, unitMeasure, disabled } = props
  const isRequired = false
  const { size } = useViewport()
  const {
    error: unitValueError,
    value: unitValue,
    onChange: handleUnitChange,
    setValue: setUnitValue
  } = useInput({
    errorCallbacks: [checkInputErrorRequired()]
  })

  const {
    value: subtotalValue,
    onChange: handleSubtotalChange,
    setValue: setSubtotalValue
  } = useInput({
    initialValue: subtotal
  })

  const { value: excludeValue, onChange: handleExcludeChange } = useCheckBox({
    changeCallback: updatedValue => {
      setSubtotal('', id, !updatedValue, 0)
      if (!updatedValue) {
        return
      }

      setSubtotalValue('')
      setUnitValue('')
    }
  })

  useEffect(() => {
    let aux
    if (typeCurrency === 'CLP') {
      aux = unitValue.replace(/[^0-9]/g, '')
    } else {
      aux = unitValue.replace(/[^0-9,]/g, '').replace(',', '.')
    }

    if (isNaN(aux) || aux === '') {
      setUnitValue('')
      setSubtotal('', id, !excludeValue, 0)
      setSubtotalValue('')
      return
    }

    const validationAfterComma = aux.split('.')
    if (aux.slice(-1) === ',' || aux.slice(-1) === '.') {
      if (validationAfterComma.length > 2) {
        aux = aux.slice(0, -1)
      } else if (validationAfterComma.length === 2 && validationAfterComma[1] === '' && unitValue.slice(-1) === ',') {
        return
      }
    }

    setUnitValue(formatTypeCurrency(typeCurrency, aux))

    switch (typeCurrency) {
      case 'CLP':
        setSubtotal(parseInt(aux, 10) * parseInt(quantity, 10), id, !excludeValue, aux)
        break
      case 'USD':
        setSubtotal(parseFloat(aux) * parseInt(quantity, 10), id, !excludeValue, aux)
        break
      default:
        setSubtotal(parseFloat(aux) * parseInt(quantity, 10), id, !excludeValue, aux)
        break
    }
    setSubtotalValue(aux * quantity)
  }, [unitValue])

  useEffect(() => {
    if (subtotal === '') {
      setSubtotalValue('')
      setSubtotalValue(formatTypeCurrency(typeCurrency, 0))
      return
    }

    setSubtotalValue(formatTypeCurrency(typeCurrency, subtotal))
  }, [subtotal])

  return (
    <Card color="blank" enableBorder margin={margin}>
      <GridContainer spacing={3}>
        <GridItem md={5} xs={12}>
          <GridContainer>
            <GridItem xs={12}>
              <Typography
                color="black1"
                display="inline"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="18px"
                variant="body2"
              >
                {name}
              </Typography>
              <Typography
                color="black3"
                display="inline"
                fontSize="14px"
                lineHeight="18px"
                margin="0 0 0 8px"
                variant="body2"
              >
                ID: {id}
              </Typography>
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12}>
              <Typography color="black1" fontSize="14px" lineHeight="18px" margin="4px 0 0" variant="body2">
                {description}
              </Typography>
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem xs={12}>
              {isRequired && (
                <CheckBox
                  disabled={disabled}
                  checked={excludeValue}
                  label="No incluir este producto en mi cotización"
                  margin="8px 0 0"
                  onChange={handleExcludeChange}
                />
              )}
            </GridItem>
          </GridContainer>
        </GridItem>
        <GridItem md={2} xs={12}>
          <GridContainer alignItems="center" height="100%">
            <GridItem align={size.isDownToTiny || size.isDownToSmall ? 'left' : 'right'} xs={12}>
              <Typography color="black3" fontSize="14px" lineHeight="18px" variant="body2">
                Cantidad
              </Typography>
              <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="26px" variant="body1">
                {`${quantity} ${unitMeasure}`}
              </Typography>
            </GridItem>
          </GridContainer>
        </GridItem>
        <GridItem md={3} xs={12}>
          <GridContainer alignItems="center" height="100%">
            <GridItem xs={12}>
              <Input
                disabled={disabled ? true : excludeValue}
                error={excludeValue ? '' : unitValueError}
                label="Valor unitario"
                placeholder={excludeValue ? 'No incluído' : ''}
                prefix={<AmountPrefix>{CURRENCY_SIGNS[typeCurrency]}</AmountPrefix>}
                value={unitValue}
                onChange={handleUnitChange}
              />
            </GridItem>
          </GridContainer>
        </GridItem>
        <GridItem md={2} xs={12}>
          <GridContainer alignItems="center" height="100%">
            <GridItem xs={12}>
              <Input
                disabled
                label="Subtotal"
                placeholder={excludeValue ? 'No incluído' : ''}
                value={subtotalValue}
                onChange={handleSubtotalChange}
              />
            </GridItem>
          </GridContainer>
        </GridItem>
      </GridContainer>
    </Card>
  )
}

QuoteBidProductsList.defaultProps = {
  subtotal: 0
}

QuoteBidProductsList.propTypes = {
  description: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  setSubtotal: PropTypes.func.isRequired,
  subtotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  typeCurrency: PropTypes.string.isRequired,
  unitMeasure: PropTypes.string.isRequired,
  disabled: PropTypes.bool
}

export default QuoteBidProductsList
